import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import RejectReasonDialog from '../RejectReasonDialog';
import { useNotification } from '../../contexts/NotificationContext';
import { useApprovalCounts } from '../../contexts/ApprovalCountsContext';
import { timesheetService } from '../../services/timesheetService';
import { ExpenseClaim, expenseClaimsService } from '../../services/expenseClaimsService';
import { PersonMonthRow, toDateKey } from './approvals.utils';

interface UseApprovalDecisionsOptions {
  /** People of the month on screen: an approval may also cover their submitted expense claims. */
  rows: PersonMonthRow[];
  /** yyyy-MM of the month under review */
  monthKey: string;
  usersById: Map<number, { name: string }>;
  /** Reloads the month after a decision. */
  refetch: () => Promise<void>;
  /** Decided ids leave the caller's selection; the ones that failed stay in it. */
  onSettled?: (ids: number[], failed: { id: number }[]) => void;
}

/**
 * Approve / reject flow shared by the approvals list and the person page: the reject-reason
 * dialog, the "approve together with the expense claims" confirmation, the notifications and the
 * sidebar counters refresh. Render `dialogs` once in the page.
 */
export function useApprovalDecisions({ rows, monthKey, usersById, refetch, onSettled }: UseApprovalDecisionsOptions) {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { refreshCounts } = useApprovalCounts();
  const [processing, setProcessing] = useState(false);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTargetIds, setRejectTargetIds] = useState<number[]>([]);

  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalTargetIds, setApprovalTargetIds] = useState<number[]>([]);
  const [approvalExpenses, setApprovalExpenses] = useState<ExpenseClaim[]>([]);
  const [approvalWithExpenses, setApprovalWithExpenses] = useState(false);
  const [approvalExpenseTotal, setApprovalExpenseTotal] = useState(0);

  const submitApproval = async (ids: number[], withExpenses: boolean) => {
    setProcessing(true);
    try {
      const result = withExpenses
        ? await timesheetService.updateStateWithExpenses(ids, 'approved', true)
        : await timesheetService.updateState(ids, 'approved');

      if (result.failed.length === 0) {
        showNotification(t('approvals.notifications.approved', 'Entries approved successfully'), 'success');
      } else if (result.succeeded.length === 0) {
        showNotification(t('approvals.errors.approve', 'Failed to approve entries'), 'error');
      } else {
        showNotification(
          t('approvals.notifications.approvedPartial', '{{success}} approved, {{failed}} could not be approved', {
            success: result.succeeded.length,
            failed: result.failed.length,
          }),
          'warning',
        );
      }
      onSettled?.(ids, result.failed);
      setApprovalDialogOpen(false);
      await refetch();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to approve entries:', error);
      showNotification(t('approvals.errors.approve', 'Failed to approve entries'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const beginApprove = async (ids: number[]) => {
    if (ids.length === 0) return;
    setProcessing(true);
    try {
      // Approving with expenses lets the backend approve every submitted claim of the month for the
      // same people, so the confirmation lists exactly those claims.
      const userIds = new Set(rows.filter((row) => row.pendingIds.some((id) => ids.includes(id))).map((row) => row.userId));
      const claims = (await expenseClaimsService.getPending()).filter(
        (claim) => userIds.has(claim.userId) && claim.status === 'SUBMITTED' && toDateKey(claim.date).startsWith(monthKey),
      );

      if (claims.length > 0) {
        setApprovalTargetIds(ids);
        setApprovalExpenses(claims);
        setApprovalExpenseTotal(claims.reduce((sum, claim) => sum + Number(claim.amount), 0));
        setApprovalWithExpenses(false);
        setApprovalDialogOpen(true);
        setProcessing(false);
        return;
      }
    } catch (error) {
      console.error('Failed to check expenses:', error);
    }
    await submitApproval(ids, false);
  };

  const beginReject = (ids: number[]) => {
    if (ids.length === 0) return;
    setRejectTargetIds(ids);
    setRejectDialogOpen(true);
  };

  const handleReject = async (reason: string) => {
    const ids = rejectTargetIds;
    if (ids.length === 0) return;
    setProcessing(true);
    try {
      const result = await timesheetService.updateState(ids, 'rejected', reason);
      if (result.failed.length === 0) {
        showNotification(t('approvals.notifications.rejected', 'Entries rejected'), 'success');
      } else if (result.succeeded.length === 0) {
        showNotification(t('approvals.errors.reject', 'Failed to reject entries'), 'error');
      } else {
        showNotification(
          t('approvals.notifications.rejectedPartial', '{{success}} rejected, {{failed}} could not be rejected', {
            success: result.succeeded.length,
            failed: result.failed.length,
          }),
          'warning',
        );
      }
      onSettled?.(ids, result.failed);
      setRejectDialogOpen(false);
      setRejectTargetIds([]);
      await refetch();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to reject entries:', error);
      showNotification(t('approvals.errors.reject', 'Failed to reject entries'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const dialogs = (
    <>
      <RejectReasonDialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectTargetIds([]);
        }}
        onConfirm={handleReject}
        entryCount={rejectTargetIds.length}
        loading={processing}
      />

      {/* Approval with expenses */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/30">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-200 dark:bg-orange-900">
                <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <DialogTitle className="text-orange-900 dark:text-orange-100">{t('approvals.expensesAlert.title')}</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 border-t border-orange-200 pt-4 dark:border-orange-800">
            <DialogDescription className="text-orange-900 dark:text-orange-100">
              {t('approvals.expensesAlert.alert', 'Warning! You have {{count}} expense claims to approve together with the timesheet.', {
                count: approvalExpenses.length,
              })}
            </DialogDescription>

            <div className="rounded-lg border border-orange-200 bg-white p-3 dark:border-orange-800 dark:bg-orange-950/50">
              <p className="mb-3 text-sm font-medium text-orange-900 dark:text-orange-100">{t('approvals.expensesAlert.detail')}</p>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {approvalExpenses.map((expense) => (
                  <div key={expense.id} className="flex justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate text-muted-foreground">
                      {format(parseISO(toDateKey(expense.date)), 'dd/MM/yyyy')} · {usersById.get(expense.userId)?.name ?? `#${expense.userId}`} · {expense.category}
                    </span>
                    <span className="font-medium tabular-nums text-orange-600 dark:text-orange-400">EUR {Number(expense.amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-between border-t border-orange-200 pt-2 dark:border-orange-800">
                <span className="text-sm font-semibold text-orange-900 dark:text-orange-100">{t('approvals.expensesAlert.total')}</span>
                <span className="text-base font-bold tabular-nums text-orange-600 dark:text-orange-400">EUR {approvalExpenseTotal.toFixed(2)}</span>
              </div>
            </div>

            <div className="rounded-lg border border-orange-200 bg-orange-100 p-3 dark:border-orange-800 dark:bg-orange-900/50">
              <div className="flex items-center space-x-2">
                <Checkbox id="approve-expenses" checked={approvalWithExpenses} onCheckedChange={(checked) => setApprovalWithExpenses(Boolean(checked))} />
                <Label htmlFor="approve-expenses" className="cursor-pointer text-sm font-medium text-orange-900 dark:text-orange-100">
                  {t('approvals.expensesAlert.approveCombined', 'I approve the expense reimbursement of EUR {{amount}}', {
                    amount: approvalExpenseTotal.toFixed(2),
                  })}
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setApprovalDialogOpen(false)} disabled={processing}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => submitApproval(approvalTargetIds, approvalWithExpenses)}
              disabled={processing || !approvalWithExpenses}
              className="bg-orange-600 hover:bg-orange-700 dark:bg-orange-600 dark:hover:bg-orange-700"
            >
              {processing ? t('common.saving', 'Approving...') : t('approvals.approve')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return { processing, beginApprove, beginReject, dialogs };
}
